import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  UsersIcon,
  CalendarIcon,
  TrophyIcon,
  PhotoIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { 
  TrophyIcon as TrophyIconSolid,
  StarIcon as StarIconSolid 
} from '@heroicons/react/24/solid';

const SportsManagement = () => {
  const { user, token, isAuthenticated } = useAuth();
  const [sports, setSports] = useState([]);
  const [coaches, setCoaches] = useState([]);
  
  // Debug coaches state changes
  useEffect(() => {
    console.log('🏈 Coaches state updated:', coaches);
  }, [coaches]);
  const [loading, setLoading] = useState(true);
  const [selectedSport, setSelectedSport] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingSport, setEditingSport] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    totalPlayers: 0,
    upcomingGames: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    season: '',
    gender: '',
    coachId: '',
    homeVenue: '',
    requirements: '',
    equipment: '',
    tryoutInfo: '',
    isActive: true,
    assistantCoaches: []
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    console.log('🔍 Auth status:', { isAuthenticated, token: !!token, hasLocalToken: !!localStorage.getItem('authToken') });
    console.log('🚀 SportsManagement useEffect triggered');
    if (isAuthenticated && (token || localStorage.getItem('authToken'))) {
      console.log('✅ Authentication check passed, calling fetch functions');
      console.log('🚀 About to call fetchCoaches...');
      fetchSportsData();
      fetchCoaches();
      console.log('🚀 All fetch functions called');
    } else {
      console.log('❌ Authentication check failed');
    }
  }, [isAuthenticated, token]);

  const fetchSportsData = async () => {
    try {
      const response = await fetch('/api/sports');
      if (response.ok) {
        const data = await response.json();
        const sportsData = data.sports || [];
        
        // Fetch player counts and upcoming games for each sport
        const sportsWithCounts = await Promise.all(
          sportsData.map(async (sport) => {
            try {
              const [playersResponse, gamesResponse] = await Promise.all([
                fetch(`/api/players/sport/${sport.id}`),
                fetch(`/api/schedules/upcoming?sportId=${sport.id}&type=game&limit=50`)
              ]);
              
              const playerCount = playersResponse.ok ? 
                (await playersResponse.json()).players?.length || 0 : 0;
              
              const upcomingGames = gamesResponse.ok ? 
                (await gamesResponse.json()).schedules?.length || 0 : 0;
              
              return {
                ...sport,
                playerCount,
                upcomingGames
              };
            } catch (error) {
              console.error(`Error fetching data for sport ${sport.id}:`, error);
              return { ...sport, playerCount: 0, upcomingGames: 0 };
            }
          })
        );
        
        setSports(sportsWithCounts);
      } else {
        // Fallback to mock data
        loadMockSports();
      }
    } catch (error) {
      console.error('Error fetching sports:', error);
      loadMockSports();
    } finally {
      setLoading(false);
    }
  };

  const fetchCoaches = async () => {
    try {
      console.log('🏈 Fetching coaches...');
      console.log('🏈 Auth status:', { isAuthenticated, hasToken: !!token, hasLocalToken: !!localStorage.getItem('authToken') });
      
      const authToken = token || localStorage.getItem('authToken');
      if (!authToken) {
        console.error('🏈 No authentication token available');
        return;
      }
      
      const response = await fetch('/api/admin/users?role=coach&isActive=true', {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      console.log('🏈 Response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('🏈 Coaches data received:', data);
        console.log('🏈 Setting coaches:', data.users || []);
        setCoaches(data.users || []);
      } else {
        console.error('🏈 Failed to fetch coaches, status:', response.status);
        const errorText = await response.text();
        console.error('🏈 Error response:', errorText);
      }
    } catch (error) {
      console.error('🏈 Error fetching coaches:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      // Fetch aggregated dashboard statistics from proper endpoints
      const [playersRes, schedulesRes] = await Promise.all([
        fetch('/api/players'),
        fetch('/api/schedules/upcoming?limit=50&type=game')
      ]);

      const playersData = playersRes.ok ? await playersRes.json() : { pagination: { totalItems: 0 } };
      const schedulesData = schedulesRes.ok ? await schedulesRes.json() : { schedules: [] };

      setDashboardData({
        totalPlayers: playersData.pagination?.totalItems || 0,
        upcomingGames: schedulesData.schedules?.length || 0
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Set empty values on error
      setDashboardData({
        totalPlayers: 0,
        upcomingGames: 0
      });
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/admin/dashboard', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Transform the activity data to include icons and colors
        const activityWithIcons = (data.recentActivity || []).map(activity => {
          let icon, color;
          switch (activity.type) {
            case 'news':
              icon = DocumentTextIcon;
              color = 'text-purple-600';
              break;
            case 'event':
              icon = CalendarIcon;
              color = 'text-blue-600';
              break;
            case 'player':
              icon = UsersIcon;
              color = 'text-green-600';
              break;
            default:
              icon = TrophyIcon;
              color = 'text-yellow-600';
          }
          return {
            ...activity,
            message: `${activity.title}: ${activity.description}`,
            icon,
            color
          };
        });
        setRecentActivity(activityWithIcons);
      } else {
        console.error('Failed to fetch recent activity:', response.statusText);
        setRecentActivity([]);
      }
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      setRecentActivity([]);
    }
  };

  const fetchNotifications = async () => {
    try {
      // Mock notifications
      const mockNotifications = [
        {
          id: 1,
          type: 'warning',
          title: 'Missing Player Photos',
          message: '3 players in Football team need profile photos',
          sport: 'Football',
          priority: 'medium',
          timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 2,
          type: 'info',
          title: 'Schedule Update Required',
          message: 'Basketball practice schedule needs confirmation',
          sport: 'Basketball',
          priority: 'low',
          timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 3,
          type: 'success',
          title: 'Roster Complete',
          message: 'Soccer team roster is now complete with all player information',
          sport: 'Soccer',
          priority: 'low',
          timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString()
        }
      ];
      setNotifications(mockNotifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const loadMockSports = () => {
    const mockSports = [
      {
        id: 1,
        name: 'Football',
        slug: 'football',
        season: 'fall',
        gender: 'boys',
        isActive: true,
        coachName: 'Coach Johnson',
        playerCount: 22,
        upcomingGames: 3,
        recentResults: 5
      },
      {
        id: 2,
        name: 'Basketball',
        slug: 'basketball',
        season: 'winter',
        gender: 'boys',
        isActive: true,
        coachName: 'Coach Williams',
        playerCount: 15,
        upcomingGames: 2,
        recentResults: 8
      },
      {
        id: 3,
        name: 'Soccer',
        slug: 'soccer',
        season: 'spring',
        gender: 'boys',
        isActive: true,
        coachName: 'Coach Davis',
        playerCount: 18,
        upcomingGames: 1,
        recentResults: 3
      },
      {
        id: 4,
        name: 'Tennis',
        slug: 'tennis',
        season: 'spring',
        gender: 'mixed',
        isActive: true,
        coachName: 'Coach Martinez',
        playerCount: 12,
        upcomingGames: 2,
        recentResults: 4
      }
    ];
    setSports(mockSports);
  };

  const handleDeleteSport = async (sportId) => {
    if (window.confirm('Are you sure you want to delete this sport? This action cannot be undone.')) {
      try {
        const response = await fetch(`/api/sports/${sportId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          toast.success('Sport deleted successfully');
          // Refresh the sports data
          fetchSportsData();
          fetchDashboardData();
        } else {
          const errorData = await response.json();
          toast.error(errorData.message || 'Failed to delete sport');
        }
      } catch (error) {
        console.error('Error deleting sport:', error);
        toast.error('An error occurred while deleting the sport');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated || !token) {
      alert('You must be logged in to perform this action. Please log in and try again.');
      return;
    }
    
    try {
      const url = editingSport ? `/api/sports/${editingSport.id}` : '/api/sports';
      const method = editingSport ? 'PUT' : 'POST';
      
      // Filter out empty string values for optional fields
      const cleanedFormData = { ...formData };
      Object.keys(cleanedFormData).forEach(key => {
        if (cleanedFormData[key] === '') {
          delete cleanedFormData[key];
        }
      });
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(cleanedFormData)
      });

      if (response.ok) {
        const sportData = await response.json();
        const sport = sportData.sport || sportData;
        
        // Upload image if one was selected
        if (selectedImage) {
          const imageUrl = await uploadImage(sport.id);
          if (imageUrl) {
            sport.featuredImage = imageUrl;
          }
        }
        
        if (editingSport) {
          setSports(sports.map(s => 
            s.id === editingSport.id ? sport : s
          ));
        } else {
          setSports([...sports, sport]);
        }
        handleCloseModal();
        alert(editingSport ? 'Sport updated successfully!' : 'Sport created successfully!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        
        let errorMessage = errorData.message || `Failed to ${editingSport ? 'update' : 'create'} sport`;
        
        // If there are validation errors, show them
        if (errorData.errors && Array.isArray(errorData.errors)) {
          const validationErrors = errorData.errors.map(err => err.msg).join(', ');
          errorMessage = `Validation failed: ${validationErrors}`;
        }
        
        console.error('Failed to save sport:', errorMessage);
        alert(errorMessage);
      }
    } catch (error) {
      console.error('Error saving sport:', error);
      alert('Network error occurred. Please try again.');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSport(null);
    setSelectedImage(null);
    setImagePreview(null);
    setFormData({
      name: '',
      description: '',
      season: '',
      gender: '',
      coachId: '',
      homeVenue: '',
      requirements: '',
      equipment: '',
      tryoutInfo: '',
      isActive: true,
      assistantCoaches: []
    });
  };

  const handleEditSport = (sport) => {
    setEditingSport(sport);
    setSelectedImage(null);
    // Use signedUrl if available (for S3 images), otherwise use featuredImage
    setImagePreview(sport.signedUrl || sport.featuredImage || null);
    setFormData({
      name: sport.name || '',
      description: sport.description || '',
      season: sport.season || '',
      gender: sport.gender || '',
      coachId: sport.coachId || '',
      homeVenue: sport.homeVenue || '',
      requirements: sport.requirements || '',
      equipment: sport.equipment || '',
      tryoutInfo: sport.tryoutInfo || '',
      isActive: sport.isActive !== undefined ? sport.isActive : true,
      assistantCoaches: Array.isArray(sport.assistantCoaches) ? sport.assistantCoaches : []
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    console.log('handleImageChange called', e.target.files);
    const file = e.target.files[0];
    if (file) {
      console.log('File selected:', file.name, file.size, file.type);
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        console.log('FileReader finished, setting preview');
        setImagePreview(reader.result);
      };
      reader.onerror = (error) => {
        console.error('FileReader error:', error);
      };
      reader.readAsDataURL(file);
    } else {
      console.log('No file selected');
      setSelectedImage(null);
      setImagePreview(null);
    }
  };

  const uploadImage = async (sportId) => {
    console.log('uploadImage called with sportId:', sportId, 'selectedImage:', selectedImage);
    if (!selectedImage) {
      console.log('No selected image, returning null');
      return null;
    }

    const imageFormData = new FormData();
    imageFormData.append('image', selectedImage);
    console.log('FormData created, making API call to:', `/api/sports/${sportId}/upload-image`);

    try {
      const response = await fetch(`/api/sports/${sportId}/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: imageFormData
      });

      console.log('Upload response status:', response.status);
      if (response.ok) {
        const result = await response.json();
        console.log('Upload successful, result:', result);
        return result.imageUrl;
      } else {
        const errorText = await response.text();
        console.error('Failed to upload image, status:', response.status, 'error:', errorText);
        return null;
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      return null;
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInHours = Math.floor((now - time) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'warning':
        return ExclamationTriangleIcon;
      case 'success':
        return CheckCircleIcon;
      default:
        return ClockIcon;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'warning':
        return 'text-yellow-600 bg-yellow-100';
      case 'success':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-blue-600 bg-blue-100';
    }
  };

  // Assistant Coach Management Functions
  const addAssistantCoach = () => {
    setFormData(prev => ({
      ...prev,
      assistantCoaches: [...(Array.isArray(prev.assistantCoaches) ? prev.assistantCoaches : []), { name: '', title: '', email: '' }]
    }));
  };

  const removeAssistantCoach = (index) => {
    setFormData(prev => ({
      ...prev,
      assistantCoaches: (Array.isArray(prev.assistantCoaches) ? prev.assistantCoaches : []).filter((_, i) => i !== index)
    }));
  };

  const updateAssistantCoach = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      assistantCoaches: (Array.isArray(prev.assistantCoaches) ? prev.assistantCoaches : []).map((coach, i) => 
        i === index ? { ...coach, [field]: value } : coach
      )
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Sports Management Dashboard</h1>
            <p className="text-gray-600 mt-1">Manage all athletic programs and teams</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add New Sport
          </button>
        </div>
      </div>

      {/* Dashboard Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600">
              <UsersIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Players</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData.totalPlayers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600">
              <CalendarIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Upcoming Games</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData.upcomingGames}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sports List */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow-sm rounded-lg">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Sports Programs</h2>
              <p className="text-sm text-gray-600 mt-1">{sports.length} active programs</p>
            </div>
            
            <div className="divide-y divide-gray-200">
              {sports.map((sport) => (
                <div key={sport.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                        <TrophyIconSolid className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{sport.name}</h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                          <span className="capitalize">{sport.season} • {sport.gender}</span>
                          <span>Coach: {sport.coach ? `${sport.coach.firstName} ${sport.coach.lastName}` : 'Not assigned'}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/sports/${sport.slug}`}
                        className="p-2 text-gray-400 hover:text-gray-600"
                        title="View Public Page"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </Link>
                      <button
                        onClick={() => handleEditSport(sport)}
                        className="p-2 text-gray-400 hover:text-blue-600"
                        title="Edit Sport"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteSport(sport.id)}
                        className="p-2 text-gray-400 hover:text-red-600"
                        title="Delete Sport"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">{sport.playerCount || 0}</p>
                      <p className="text-xs text-gray-500">Players</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{sport.upcomingGames || 0}</p>
                      <p className="text-xs text-gray-500">Upcoming</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-yellow-600">{sport.recentResults || 0}</p>
                      <p className="text-xs text-gray-500">Results</p>
                    </div>
                  </div>
                  
                  {/* Management Links */}
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Link
                      to={`/admin/sports/${sport.id}/roster`}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200"
                    >
                      <UsersIcon className="h-4 w-4 mr-1" />
                      Roster
                    </Link>
                    <Link
                      to={`/admin/sports/${sport.id}/schedule`}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 hover:bg-green-200"
                    >
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      Schedule
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Notifications */}
          <div className="bg-white shadow-sm rounded-lg">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
            </div>
            <div className="divide-y divide-gray-200 max-h-80 overflow-y-auto">
              {notifications.map((notification) => {
                const Icon = getNotificationIcon(notification.type);
                return (
                  <div key={notification.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start space-x-3">
                      <div className={`p-1 rounded-full ${getNotificationColor(notification.type)}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                        <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-500">{notification.sport}</span>
                          <span className="text-xs text-gray-500">{formatTimeAgo(notification.timestamp)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white shadow-sm rounded-lg">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
            </div>
            <div className="divide-y divide-gray-200 max-h-80 overflow-y-auto">
              {recentActivity.map((activity) => {
                const Icon = activity.icon;
                return (
                  <div key={activity.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start space-x-3">
                      <div className={`p-1 rounded-full bg-gray-100 ${activity.color}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">{activity.message}</p>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-gray-500">{activity.sport}</span>
                          <span className="text-xs text-gray-500">{formatTimeAgo(activity.timestamp)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Modal for Add/Edit Sport */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingSport ? 'Edit Sport' : 'Add New Sport'}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Sport Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sport Image</label>
                  <div className="flex items-center space-x-4">
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                    </div>
                    {imagePreview && (
                      <div className="flex-shrink-0">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="h-16 w-16 object-cover rounded-lg border border-gray-300"
                        />
                      </div>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-gray-500">Upload an image for this sport (JPG, PNG, GIF, WebP - Max 5MB)</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Season *</label>
                    <select
                      name="season"
                      value={formData.season}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Select Season</option>
                      <option value="fall">Fall</option>
                      <option value="winter">Winter</option>
                      <option value="spring">Spring</option>
                      <option value="year_round">Year Round</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Gender (Optional)</label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Gender</option>
                      <option value="boys">Boys</option>
                      <option value="girls">Girls</option>
                      <option value="coed">Coed</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Head Coach (Optional)</label>
                  <select
                    name="coachId"
                    value={formData.coachId}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Head Coach</option>
                    {coaches.length === 0 && (
                      <option value="" disabled>No coaches available</option>
                    )}
                    {coaches.map(coach => {
                      console.log('🏈 Rendering coach option:', coach);
                      return (
                        <option key={coach.id} value={coach.id}>
                          {coach.firstName} {coach.lastName}
                        </option>
                      );
                    })}
                  </select>
                  <div className="text-xs text-gray-500 mt-1">
                    Debug: {coaches.length} coaches loaded
                  </div>
                </div>
                
                {/* Assistant Coaches Section */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">Assistant Coaches (Optional)</label>
                    <button
                      type="button"
                      onClick={addAssistantCoach}
                      className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <PlusIcon className="h-3 w-3 mr-1" />
                      Add Assistant Coach
                    </button>
                  </div>
                  
                  {(Array.isArray(formData.assistantCoaches) ? formData.assistantCoaches : []).map((coach, index) => (
                    <div key={index} className="border border-gray-200 rounded-md p-3 mb-2 bg-gray-50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Assistant Coach {index + 1}</span>
                        <button
                          type="button"
                          onClick={() => removeAssistantCoach(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-1 gap-2">
                        <input
                          type="text"
                          placeholder="Full Name"
                          value={coach.name}
                          onChange={(e) => updateAssistantCoach(index, 'name', e.target.value)}
                          className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                        <input
                          type="text"
                          placeholder="Title (e.g., Assistant Coach, Defensive Coordinator)"
                          value={coach.title}
                          onChange={(e) => updateAssistantCoach(index, 'title', e.target.value)}
                          className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                        <input
                          type="email"
                          placeholder="Email Address"
                          value={coach.email}
                          onChange={(e) => updateAssistantCoach(index, 'email', e.target.value)}
                          className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                    </div>
                  ))}
                  
                  {formData.assistantCoaches.length === 0 && (
                    <p className="text-sm text-gray-500 italic">No assistant coaches added yet.</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Home Venue (Optional)</label>
                  <input
                    type="text"
                    name="homeVenue"
                    value={formData.homeVenue}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter home venue location"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Requirements (Optional)</label>
                  <textarea
                    name="requirements"
                    value={formData.requirements}
                    onChange={handleInputChange}
                    rows={2}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter sport requirements"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Equipment (Optional)</label>
                  <textarea
                    name="equipment"
                    value={formData.equipment}
                    onChange={handleInputChange}
                    rows={2}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter required equipment"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tryout Information (Optional)</label>
                  <textarea
                    name="tryoutInfo"
                    value={formData.tryoutInfo}
                    onChange={handleInputChange}
                    rows={2}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter tryout information"
                  />
                </div>
                
                {/* Image Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Featured Image</label>
                  <div className="mt-1 flex items-center space-x-4">
                    <input
                      key={showModal ? 'modal-open' : 'modal-closed'}
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                  </div>
                  {imagePreview && (
                    <div className="mt-2">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="h-32 w-32 object-cover rounded-lg border border-gray-300"
                      />
                    </div>
                  )}
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    Active Sport
                  </label>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {editingSport ? 'Update' : 'Create'} Sport
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SportsManagement;